/**
 * Liberação de Visitas — PORTARIA / GESTÃO, 0.1.65.
 *
 * O porteiro: (1) registra a CHEGADA presencial (formulário rápido → notifica o
 * morador para autorizar); (2) VALIDA um convite pelo código ou pela CÂMERA (QR);
 * (3) registra ENTRADA e SAÍDA (livro digital) e imprime o relatório do dia.
 *
 * A leitura de QR usa o BarcodeDetector nativo do navegador (Chrome/Android). Onde
 * não existir, o porteiro digita o código — nada quebra.
 *
 * 0.1.88: quadro "Acesso permanente" — os autorizados que os moradores cadastraram
 * (diarista, familiar, babá…), com busca e Entrada/Saída num toque, sem pedir ao morador.
 */
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Camera, Check, DoorOpen, FileText, Loader2, LogIn, LogOut, Plus,
  ScanLine, Search, ShieldCheck, Trash2, UserCheck, X,
} from "lucide-react";
import { api, ApiError, visitasLivroPdfUrl } from "../lib/api";
import { fmtDataHora as fmt } from "../lib/datas";

interface Visita {
  id: number; unidade: string; visitante_nome: string; documento: string | null;
  placa: string | null; tipo: string | null; origem: string; observacao: string | null;
  codigo: string | null; status: string; validade_fim: string | null; recorrente: boolean;
  criado_em: string | null; aprovado_por: string | null; entrada_em: string | null; saida_em: string | null;
}
interface Resp {
  condominio_id: number; itens: Visita[]; pode_gerir: boolean;
  unidades: string[]; unidades_morador: string[]; tipos: string[]; status_possiveis: string[];
}

const STATUS_LABEL: Record<string, string> = {
  convidado: "Convite", aguardando: "Aguardando morador", autorizada: "Autorizada",
  negada: "Negada", dentro: "Dentro", encerrada: "Encerrada", expirada: "Expirada",
};
const STATUS_COR: Record<string, string> = {
  convidado: "bg-teal-100 text-teal-800", aguardando: "bg-amber-100 text-amber-800",
  autorizada: "bg-emerald-100 text-emerald-800", negada: "bg-rose-100 text-rose-700",
  dentro: "bg-blue-100 text-blue-800", encerrada: "bg-slate-100 text-slate-500",
  expirada: "bg-slate-100 text-slate-500",
};

interface FormState { unidade: string; visitante_nome: string; tipo: string; documento: string; placa: string; observacao: string }
const VAZIO: FormState = { unidade: "", visitante_nome: "", tipo: "", documento: "", placa: "", observacao: "" };

export function VisitasPortariaPage({ condominioId, onVoltar }: { condominioId: number; onVoltar: () => void }) {
  const qc = useQueryClient();
  const inval = () => qc.invalidateQueries({ queryKey: ["visitas", condominioId] });
  const [fStatus, setFStatus] = useState("");
  const { data, isLoading } = useQuery<Resp>({
    queryKey: ["visitas", condominioId, fStatus],
    queryFn: () => api.get(`/condominios/${condominioId}/visitas${fStatus ? `?status_f=${fStatus}` : ""}`),
  });

  // ---- registrar chegada presencial ----
  const [form, setForm] = useState<FormState | null>(null);
  const [erroForm, setErroForm] = useState<string | null>(null);
  const registrar = useMutation({
    mutationFn: () => {
      const f = form!;
      return api.post(`/condominios/${condominioId}/visitas/presencial`, {
        unidade: f.unidade.trim(), visitante_nome: f.visitante_nome.trim(),
        tipo: f.tipo.trim() || null, documento: f.documento.trim() || null,
        placa: f.placa.trim() || null, observacao: f.observacao.trim() || null,
      });
    },
    onSuccess: () => { setForm(null); setErroForm(null); inval(); },
    onError: (e) => setErroForm(e instanceof ApiError ? e.message : "Não foi possível registrar."),
  });

  // ---- validar convite (código/QR) ----
  const [codigo, setCodigo] = useState("");
  const [validada, setValidada] = useState<Visita | null>(null);
  const [erroVal, setErroVal] = useState<string | null>(null);
  const [scan, setScan] = useState(false);
  const validar = useMutation({
    mutationFn: (cod: string) => api.post<Visita>(`/condominios/${condominioId}/visitas/validar`, { codigo: cod.trim() }),
    onSuccess: (v) => { setValidada(v); setErroVal(null); },
    onError: (e) => { setValidada(null); setErroVal(e instanceof ApiError ? e.message : "Código inválido."); },
  });

  // ---- entrada / saída ----
  const entrada = useMutation({
    mutationFn: (id: number) => api.post(`/visitas/${id}/entrada`, {}),
    onSuccess: () => { setValidada(null); setCodigo(""); inval(); },
    onError: (e) => setErroVal(e instanceof ApiError ? e.message : "Não foi possível registrar a entrada."),
  });
  const saida = useMutation({
    mutationFn: (id: number) => api.post(`/visitas/${id}/saida`, {}),
    onSuccess: inval,
  });
  const excluir = useMutation({
    mutationFn: (id: number) => api.delete(`/visitas/${id}`),
    onSuccess: inval,
  });

  const itens = data?.itens ?? [];
  const dentro = itens.filter((v) => v.status === "dentro").length;

  return (
    <div className="max-w-2xl mx-auto px-1">
      <div className="flex items-center justify-between pt-2 pb-4">
        <button onClick={onVoltar}
          className="inline-flex items-center gap-1.5 text-sm px-2.5 py-1.5 rounded-md border text-slate-600 bg-white border-slate-200 hover:bg-slate-50">
          <ArrowLeft size={16} /> Voltar
        </button>
        <div className="flex items-center gap-2 text-slate-700 font-semibold"><ShieldCheck size={18} /> Visitas — Portaria</div>
      </div>

      {/* Validar convite (código + câmera) */}
      <div className="mb-4 p-4 rounded-xl border border-teal-200 bg-teal-50/50">
        <div className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5"><ScanLine size={16} /> Validar convite</div>
        <div className="flex gap-2">
          <input value={codigo} onChange={(e) => setCodigo(e.target.value.toUpperCase())}
            placeholder="Código (ex.: VIS-7KPQ2M)" className="flex-1 text-sm font-mono rounded-md border border-slate-300 px-3 py-2" />
          <button onClick={() => codigo.trim() && validar.mutate(codigo)} disabled={validar.isPending || !codigo.trim()}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-teal-700 text-white text-sm disabled:opacity-50">
            {validar.isPending ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Validar
          </button>
          <button onClick={() => setScan(true)} title="Escanear QR com a câmera"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-teal-300 text-teal-700 bg-white text-sm"><Camera size={16} /></button>
        </div>
        {erroVal && <div className="text-sm text-rose-700 mt-2">{erroVal}</div>}
        {validada && (
          <div className="mt-3 p-3 rounded-lg bg-white border border-teal-200">
            <div className="font-semibold text-slate-800">{validada.visitante_nome}</div>
            <div className="text-xs text-slate-500">
              Unidade {validada.unidade}{validada.tipo ? ` · ${validada.tipo}` : ""} ·{" "}
              <span className={`px-2 py-0.5 rounded-full ${STATUS_COR[validada.status] ?? ""}`}>{STATUS_LABEL[validada.status] ?? validada.status}</span>
            </div>
            {validada.status === "aguardando" ? (
              <div className="text-sm text-amber-700 mt-2">Aguardando o morador autorizar no Portal.</div>
            ) : (
              <button onClick={() => entrada.mutate(validada.id)} disabled={entrada.isPending}
                className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                {entrada.isPending ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />} Registrar ENTRADA
              </button>
            )}
          </div>
        )}
      </div>

      {/* Acesso permanente (diarista, familiar…) — 0.1.88 */}
      <AutorizadosPortaria condominioId={condominioId} onMudou={inval} />

      {/* Registrar chegada presencial */}
      {!form ? (
        <button onClick={() => { setForm({ ...VAZIO }); setErroForm(null); }}
          className="mb-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-900 text-white font-medium">
          <Plus size={18} /> Chegada sem convite (avisar morador)
        </button>
      ) : (
        <div className="mb-4 p-4 rounded-xl border border-slate-200 bg-white space-y-2.5">
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs text-slate-500 flex flex-col gap-1">Apartamento
              <input list="unidades-vis" value={form.unidade} onChange={(e) => setForm({ ...form, unidade: e.target.value })}
                placeholder="Ex.: 301" className="text-sm rounded-md border border-slate-300 px-3 py-2" />
              <datalist id="unidades-vis">{(data?.unidades ?? []).map((u) => <option key={u} value={u} />)}</datalist>
            </label>
            <label className="text-xs text-slate-500 flex flex-col gap-1">Tipo
              <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                className="text-sm rounded-md border border-slate-300 px-2 py-2 bg-white">
                <option value="">(opcional)</option>
                {(data?.tipos ?? []).map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
          </div>
          <input value={form.visitante_nome} onChange={(e) => setForm({ ...form, visitante_nome: e.target.value })}
            placeholder="Nome do visitante" className="w-full text-sm rounded-md border border-slate-300 px-3 py-2" />
          <div className="grid grid-cols-2 gap-2">
            <input value={form.documento} onChange={(e) => setForm({ ...form, documento: e.target.value })}
              placeholder="Documento (opcional)" className="text-sm rounded-md border border-slate-300 px-3 py-2" />
            <input value={form.placa} onChange={(e) => setForm({ ...form, placa: e.target.value })}
              placeholder="Placa (opcional)" className="text-sm rounded-md border border-slate-300 px-3 py-2" />
          </div>
          {erroForm && <div className="text-sm text-rose-700">{erroForm}</div>}
          <div className="flex gap-2">
            <button onClick={() => registrar.mutate()} disabled={registrar.isPending || !form.unidade.trim() || !form.visitante_nome.trim()}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-slate-800 hover:bg-slate-900 text-white text-sm disabled:opacity-50">
              {registrar.isPending ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Avisar morador
            </button>
            <button onClick={() => { setForm(null); setErroForm(null); }}
              className="px-4 py-2 rounded-md border border-slate-300 text-slate-600 text-sm">Cancelar</button>
          </div>
          <p className="text-xs text-slate-400">Se o nome estiver na lista de autorizados da unidade, já entra liberado.</p>
        </div>
      )}

      {/* Livro do dia */}
      <div className="mb-3 flex items-center justify-between">
        <span className="text-xs text-slate-500">{dentro > 0 ? `${dentro} visitante(s) dentro agora` : "Ninguém registrado dentro"}</span>
        <a href={visitasLivroPdfUrl(condominioId)} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-slate-300 text-slate-600 bg-white hover:bg-slate-50">
          <FileText size={14} /> Livro do dia (PDF)
        </a>
      </div>

      {/* Filtro + lista */}
      <div className="flex gap-1.5 mb-3 flex-wrap">
        {["", "aguardando", "autorizada", "dentro", "convidado", "encerrada"].map((s) => (
          <button key={s || "todas"} onClick={() => setFStatus(s)}
            className={`text-xs px-2.5 py-1.5 rounded-md border ${fStatus === s ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200"}`}>
            {s ? STATUS_LABEL[s] : "Todas"}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center gap-2 text-slate-500 py-14"><Loader2 size={18} className="animate-spin" /> Carregando…</div>
      ) : itens.length === 0 ? (
        <div className="text-center text-slate-400 py-14 text-sm">Nenhuma visita {fStatus ? STATUS_LABEL[fStatus].toLowerCase() : ""}.</div>
      ) : (
        <div className="space-y-2.5">
          {itens.map((v) => (
            <div key={v.id} className="p-4 rounded-xl border border-slate-200 bg-white">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-800 flex items-center gap-2">
                    <DoorOpen size={16} className="text-teal-700 flex-shrink-0" /> {v.visitante_nome}
                    <span className="text-xs font-normal text-slate-500">· Apto {v.unidade}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {v.tipo ? `${v.tipo} · ` : ""}{v.origem === "convite" ? "convite" : v.origem === "autorizado" ? "acesso permanente" : "presencial"}
                    {v.entrada_em ? ` · entrou ${fmt(v.entrada_em)}` : ""}
                    {v.saida_em ? ` · saiu ${fmt(v.saida_em)}` : ""}
                  </div>
                  <div className="mt-1.5">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${STATUS_COR[v.status] ?? "bg-slate-100"}`}>{STATUS_LABEL[v.status] ?? v.status}</span>
                  </div>
                </div>
                <div className="flex flex-col gap-1 flex-shrink-0">
                  {(v.status === "autorizada" || v.status === "convidado") && (
                    <button onClick={() => entrada.mutate(v.id)}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs"><LogIn size={14} /> Entrada</button>
                  )}
                  {v.status === "dentro" && (
                    <button onClick={() => saida.mutate(v.id)}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs"><LogOut size={14} /> Saída</button>
                  )}
                  <button title="Excluir" onClick={() => { if (confirm("Excluir este registro?")) excluir.mutate(v.id); }}
                    className="p-1.5 rounded-md text-rose-500 hover:bg-rose-50 self-end"><Trash2 size={15} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {scan && (
        <QrScanner
          onFechar={() => setScan(false)}
          onLido={(cod) => { setScan(false); setCodigo(cod.toUpperCase()); validar.mutate(cod); }}
        />
      )}
    </div>
  );
}

interface Autorizado {
  id: number; unidade_norm: string; nome: string; documento: string | null;
  tipo: string | null; observacao: string | null; dentro_visita_id: number | null; entrada_em: string | null;
}

/**
 * Acesso permanente (0.1.88): quem os moradores autorizaram a entrar SEMPRE (diarista,
 * familiar, babá, professor). A portaria procura pelo nome ou apto e registra a Entrada
 * (ou a Saída de quem está dentro) num toque — o registro vai para o livro do dia.
 */
function AutorizadosPortaria({ condominioId, onMudou }: { condominioId: number; onMudou: () => void }) {
  const qc = useQueryClient();
  const [busca, setBusca] = useState("");
  const [aberto, setAberto] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const chave = ["visitantes-autorizados", condominioId];
  const { data } = useQuery<Autorizado[]>({
    queryKey: chave,
    queryFn: () => api.get(`/condominios/${condominioId}/visitantes-autorizados`),
  });
  const atualizar = () => { setErro(null); qc.invalidateQueries({ queryKey: chave }); onMudou(); };
  const falhou = (e: unknown) => setErro(e instanceof ApiError ? e.message : "Não foi possível registrar.");
  const entrar = useMutation({
    mutationFn: (id: number) => api.post(`/visitantes-autorizados/${id}/entrada`, {}),
    onSuccess: atualizar, onError: falhou,
  });
  const sair = useMutation({
    mutationFn: (visitaId: number) => api.post(`/visitas/${visitaId}/saida`, {}),
    onSuccess: atualizar, onError: falhou,
  });

  const todos = data ?? [];
  if (todos.length === 0) return null;
  const termo = busca.trim().toLowerCase();
  const lista = todos.filter((a) => !termo
    || a.nome.toLowerCase().includes(termo) || a.unidade_norm.toLowerCase().includes(termo)
    || (a.tipo ?? "").toLowerCase().includes(termo));
  const dentroAgora = todos.filter((a) => a.dentro_visita_id).length;

  return (
    <div className="mb-4 p-4 rounded-xl border border-violet-200 bg-violet-50/40">
      <button onClick={() => setAberto((v) => !v)} className="w-full flex items-center justify-between text-left">
        <span className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
          <UserCheck size={16} /> Acesso permanente ({todos.length})
          <span className="font-normal text-xs text-slate-500">diarista, familiar, babá…</span>
        </span>
        <span className="text-xs text-violet-700">
          {dentroAgora ? `${dentroAgora} dentro · ` : ""}{aberto ? "fechar" : "abrir"}
        </span>
      </button>
      {aberto && (
        <div className="mt-3 space-y-2">
          <div className="relative">
            <Search size={15} className="absolute left-2.5 top-2.5 text-slate-400" />
            <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar por nome ou apto"
              className="w-full text-sm rounded-md border border-slate-300 pl-8 pr-3 py-2" />
          </div>
          {erro && <div className="text-sm text-rose-700">{erro}</div>}
          {lista.length === 0 ? (
            <div className="text-xs text-slate-500 py-2">Ninguém encontrado.</div>
          ) : lista.map((a) => (
            <div key={a.id} className="flex items-center justify-between gap-2 p-2.5 rounded-lg bg-white border border-violet-100">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">{a.nome}</div>
                <div className="text-xs text-slate-500">
                  Apto {a.unidade_norm}{a.tipo ? ` · ${a.tipo}` : ""}
                  {a.dentro_visita_id && a.entrada_em ? ` · dentro desde ${fmt(a.entrada_em)}` : ""}
                </div>
              </div>
              {a.dentro_visita_id ? (
                <button onClick={() => sair.mutate(a.dentro_visita_id!)} disabled={sair.isPending}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs disabled:opacity-50">
                  <LogOut size={14} /> Saída
                </button>
              ) : (
                <button onClick={() => entrar.mutate(a.id)} disabled={entrar.isPending}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs disabled:opacity-50">
                  <LogIn size={14} /> Entrada
                </button>
              )}
            </div>
          ))}
          <p className="text-xs text-slate-400">Quem cadastra e remove é o morador, no Portal (Visitas).</p>
        </div>
      )}
    </div>
  );
}

/**
 * Leitor de QR pela câmera usando o BarcodeDetector nativo. Se o navegador não
 * suportar, mostra instrução para digitar o código. Zero dependências externas.
 */
function QrScanner({ onLido, onFechar }: { onLido: (codigo: string) => void; onFechar: () => void }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let stream: MediaStream | null = null;
    let timer: number | undefined;
    let parado = false;
    // BarcodeDetector não está na tipagem padrão do DOM.
    const BD = (window as unknown as { BarcodeDetector?: new (o?: unknown) => { detect: (s: unknown) => Promise<Array<{ rawValue: string }>> } }).BarcodeDetector;

    async function iniciar() {
      if (!BD) { setErro("Este navegador não lê QR pela câmera. Digite o código."); return; }
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        const detector = new BD({ formats: ["qr_code"] });
        const tick = async () => {
          if (parado || !videoRef.current) return;
          try {
            const found = await detector.detect(videoRef.current);
            if (found && found.length && found[0].rawValue) { onLido(found[0].rawValue.trim()); return; }
          } catch { /* ignora frame ruim */ }
          timer = window.setTimeout(tick, 350);
        };
        tick();
      } catch {
        setErro("Não foi possível abrir a câmera. Verifique a permissão ou digite o código.");
      }
    }
    iniciar();
    return () => {
      parado = true;
      if (timer) window.clearTimeout(timer);
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
  }, [onLido]);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={onFechar}>
      <div className="bg-white rounded-2xl p-4 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-bold text-slate-900">Escanear QR</h3>
          <button onClick={onFechar} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100"><X size={18} /></button>
        </div>
        {erro ? (
          <div className="text-sm text-slate-600 py-8 text-center">{erro}</div>
        ) : (
          <video ref={videoRef} playsInline muted className="w-full rounded-lg bg-black aspect-square object-cover" />
        )}
        <p className="text-xs text-slate-400 mt-2 text-center">Aponte para o QR do convite do visitante.</p>
      </div>
    </div>
  );
}
